import JSONbig from 'json-bigint';
import _lang from 'lodash/lang';
import _array from 'lodash/array';
import _object from 'lodash/object';
import _function from 'lodash/function';
import _number from 'lodash/number';
import _string from 'lodash/string';
import _util from 'lodash/util';
import _collection from 'lodash/collection';
import uuid from 'uuid/v4';
import Static from './Static';

const canvas = document.createElement('canvas');
const ctx = canvas.getContext('2d');

/**
 * 帮助类，静态
 */
export default class Util extends Static {
    static isNaN(value) {
        return _lang.isNaN(value);
    }

    static isNull(value) {
        return _lang.isNull(value);
    }

    static isUndefined(value) {
        return _lang.isUndefined(value);
    }

    static isNil(value) {
        return _lang.isNil(value);
    }

    static isNumber(value) {
        return _lang.isNumber(value);
    }

    static isInteger(value) {
        return _lang.isInteger(value);
    }

    static isString(value) {
        return _lang.isString(value);
    }

    static isBoolean(value) {
        return _lang.isBoolean(value);
    }

    static isFunction(value) {
        return _lang.isFunction(value);
    }

    static isArray(value) {
        return _lang.isArray(value);
    }

    static isObject(value) {
        return (!_lang.isArray(value)) && _lang.isObject(value);
    }

    static isPlainObject(value) {
        return _lang.isPlainObject(value);
    }

    static isMap(value) {
        return _lang.isMap(value);
    }

    static isSet(value) {
        return _lang.isSet(value);
    }

    static isElement(value) {
        return _lang.isElement(value);
    }

    static isEmpty(value) {
        return _lang.isEmpty(value);
    }

    static isEqual(value, other) {
        return _lang.isEqual(value, other);
    }

    static isEqualWith(value, other, func) {
        return _lang.isEqualWith(value, other);
    }

    static clone(value, isDeep = true) {
        if (isDeep) {
            return _lang.cloneDeep(value);
        }

        return _lang.clone(value);
    }

    static toArray(str) {
        return _lang.toArray(str);
    }

    static sortBy(array, other) {
        return _collection.sortBy(array, other);
    }

    static groupBy(array, other) {
        return _collection.groupBy(array, other);
    }

    /* 以下为数组相关方法 */
    static first(array) {
        return _array.first(array);
    }

    static last(array) {
        return _array.last(array);
    }

    static concat(array, other) {
        return _array.concat(array, other);
    }

    static remove(array, func) {
        return _array.remove(array, func);
    }

    static reverse(array) {
        return _array.reverse(array);
    }

    static uniq(array) {
        return _array.uniq(array);
    }

    static uniqBy(array, func) {
        return _array.uniqBy(array, func);
    }

    static uniqWith(array, func) {
        return _array.uniqWith(array, func);
    }

    static union(array, other) {
        return _array.union(array, other);
    }

    static unionBy(array, other, func) {
        return _array.unionBy(array, other, func);
    }

    static unionWith(array, other, func) {
        return _array.unionWith(array, other, func);
    }

    static difference(array, other) {
        return _array.difference(array, other);
    }

    static differenceBy(array, other, identity) {
        return _array.differenceBy(array, other, identity);
    }

    static differenceWith(array, other, comparator) {
        return _array.differenceWith(array, other, comparator);
    }

    static intersection(array, other) {
        return _array.intersection(array, other);
    }

    static intersectionBy(array, other, identity) {
        return _array.intersectionBy(array, other, identity);
    }

    static intersectionWith(array, other, comparator) {
        return _array.intersectionWith(array, other, comparator);
    }

    static flatten(array) {
        return _array.flatten(array);
    }

    /* 以下为对象相关方法 */

    static assign(object, source) {
        return _object.assign(object, source);
    }

    static assignWith(object, source, func) {
        return _object.assignWith(object, source, func);
    }

    static merge(object, source) {
        return _object.merge(object, source);
    }

    static mergeWith(object, source, func) {
        return _object.mergeWith(object, source, func);
    }

    static get(object, path) {
        return _object.get(object, path);
    }

    static set(object, path, value) {
        return _object.set(object, path, value);
    }

    static findKey(object, func) {
        return _object.findKey(object, func);
    }

    static findLastKey(object, func) {
        return _object.findLastKey(object, func);
    }

    static forOwn(object, func) {
        return _object.forOwn(object, func);
    }

    static forIn(object, func) {
        return _object.forIn(object, func);
    }

    static keys(object) {
        return _object.keys(object);
    }

    static keysIn(object) {
        return _object.keysIn(object);
    }

    static values(object) {
        return _object.values(object);
    }

    static valuesIn(object) {
        return _object.valuesIn(object);
    }

    static has(object, path) {
        return _object.has(object, path);
    }

    static hasIn(object, path) {
        return _object.hasIn(object, path);
    }

    static mapKeys(object, func) {
        return _object.mapKeys(object, func);
    }

    static mapValues(object, func) {
        return _object.mapValues(object, func);
    }

    static unset(object, path) {
        return _object.unset(object, path);
    }

    static invertBy(object, func) {
        return _object.invertBy(object, func);
    }

    /* 以下为其他方法 */

    static delay(wait, func, args) {
        const promise = new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                if (func) {
                    func(args);
                }
                clearTimeout(timeout);
                resolve();
            }, wait);
        });
        return promise;
    }

    static bind(func, context) {
        return _function.bind(func, context);
    }

    static bindAll(object, methodNames) {
        let allMethodNames = methodNames;
        if (!allMethodNames) {
            allMethodNames = [];
            let target = object;
            let allPropertyNames = [];

            while (target) {
                const propertyNames = Object.getOwnPropertyNames(target);
                allPropertyNames = allPropertyNames.concat(propertyNames);

                target = Object.getPrototypeOf(target);
            }

            allPropertyNames.forEach(propertyName => {
                const property = object[propertyName];

                // 跳过非函数的属性
                if (!Util.isFunction(property)) {
                    return;
                }

                allMethodNames.push(propertyName);
            });

            allPropertyNames = Util.uniq(allPropertyNames);
        }
        return _util.bindAll(object, allMethodNames);
    }

    static random(start, end) {
        return _number.random(start, end, false);
    }

    static escape(str) {
        return _string.escape(str);
    }

    static unescape(str) {
        return _string.unescape(str);
    }

    static truncate(str, length, omission = '...') {
        return _string.truncate(str, {
            length: length,
            omission: omission
        });
    }

    static uniqueId(prefix = '') {
        return _util.uniqueId(prefix);
    }

    static uuid() {
        return uuid();
    }

    /**
     * 返回数组中重复的值
     * @param   {Array} arr  数组
     * @returns {Array}    重复元素
     */
    static getRepeat(arr) {
        let ids = [];
        const obj = {};
        for (let i = 0; i < arr.length; i++) {
            if (obj[arr[i]]) {
                ids.push(arr[i]);
            } else {
                obj[arr[i]] = true;
            }
        }
        ids = this.uniq(ids);
        return ids;
    }

    /**
     * 计算字符数，一个中文代表两个字符
     * -author ChenXiao
     * -date 2018-03-06
     * @param  {[type]} param [description]
     * @return {[type]}       [description]
     */
    static getBLength(param) {
        let len = 0;
        for (let i = 0; i < param.length; i++) {
            if (param.charCodeAt(i) > 127 || param.charCodeAt(i) == 94) {
                len += 2;
            } else {
                len++;
            }
        }
        return len;
    }

    /**
     * 十进制转二进制
     * @param   {number} dec 十进制数字
     * @returns {string}   二进制字符串
     */
    static dec2bin(dec) {
        let num = dec;
        let bin = '';
        while (num > 0) {
            if (num % 2 !== 0) {
                bin = `1${bin}`;
            } else {
                bin = `0${bin}`;
            }
            num = Math.floor(num / 2);
        }
        return bin;
    }

    /**
     * 二进制转10进制
     * @param   {string} bin      二进制字符串
     * @returns {number} 十进制数字
     */
    static bin2dec(bin) {
        const c = bin.split('');
        const len = c.length;
        let dec = 0;
        for (let i = 0; i < len; i++) {
            let temp = 1;
            if (parseInt(c[i], 10) === 1) {
                for (let j = i + 1; j < len; j++) {
                    temp *= 2;
                }
                dec += temp;
            }
        }
        return dec;
    }

    /**
     * 日期时间格式化
     * @param  {String} str    纯数字日期时间
     * @param  {string} concat 日期连接符，默认'-'
     * @return {string}        格式化之后的日期时间
     */
    static dateFormat(str, concat) {
        let ret;
        let cat;
        if (concat) {
            cat = concat;
        } else {
            cat = '-';
        }
        if (str.length < 14) {
            if (str.length === 8) {
                ret = str.substr(0, 4) + cat + str.substr(4, 2) + cat + str.substr(6, 2);
            } else {
                ret = str;
            }
        } else { // yyyy-mm-dd hh:mi:ss
            ret = `${str.substr(0, 4) + cat + str.substr(4, 2) + cat + str.substr(6, 2)} ${
                str.substr(8, 2)}:${str.substr(10, 2)}:${str.substr(12, 2)}`;
        }
        return ret;
    }

    /**
     * 半角转全角
     * @param {*} txtstring
     */
    static ToDBC(txtstring) { // 半角转全角
        if (txtstring == null || txtstring == '' || txtstring == ' ') {
            return '';
        }
        let tmp = '';
        for (let i = 0; i < txtstring.length; i++) {
            if (txtstring.charCodeAt(i) == 32) {
                tmp += String.fromCharCode(12288);
            } else if (txtstring.charCodeAt(i) == 160) { // 处理下Unicode为160的特殊空格
                tmp += String.fromCharCode(12288);
            } else if (txtstring.charCodeAt(i) < 127) {
                tmp += String.fromCharCode(txtstring.charCodeAt(i) + 65248);
            } else {
                tmp += String.fromCharCode(txtstring.charCodeAt(i));
            }
        }
        return tmp;
    }

    /**
     * 全角转半角
     * @param {string} str 全角字符串
     */
    static ToCDB(str) { // 全角转半角
        let tmp;
        if (str) {
            tmp = '';
            for (let i = 0; i < str.length; i++) {
                if (str.charCodeAt(i) == 12288) {
                    tmp += String.fromCharCode(32);
                } else if (str.charCodeAt(i) == 160) {
                    tmp += String.fromCharCode(32); // 处理下Unicode为160的特殊空格
                } else if (str.charCodeAt(i) > 65248 && str.charCodeAt(i) < 65375) {
                    tmp += String.fromCharCode(str.charCodeAt(i) - 65248);
                } else {
                    tmp += str[i];
                }
            }
        } else {
            tmp = str;
        }
        return tmp;
    }

    /**
     * 将整数转换为指定长度的二进制数组，转换后如果长度不够，则向左补0
     * @param  {number} dec  待转换的整数
     * @param  {number} size 数组长度
     * @return {array<0|1>}  二进制数组
     */
    static decimal2BinaryArray(dec, size) {
        const num = +dec;
        const arr = num.toString(2).split('');
        arr.forEach((it, idx) => {
            arr[idx] = parseInt(it, 10);
        });
        for (let i = 0, len = arr.length; i < size - len; i++) {
            arr.unshift(0);
        }
        return arr;
    }

    /**
     * 将二进制数组转换为整数
     * @param  {array<0|1>} arr 存储二进制的数组
     * @return {number}         整数
     */
    static binaryArray2Decimal(arr) {
        return parseInt(arr.join(''), 2);
    }

    /**
     * 扩展对象方法
     * @param {object}        dest 扩展的目标对象
     * @param {[object, ...]} rest 被用于扩展的对象
     * @return {undefined}
     */
    static extend(dest, ...rest) { // (Object[, Object, ...]) ->
        for (let i = 0, len = rest.length; i < len; i++) {
            const src = rest[i] || {};
            for (const k in src) {
                if (src.hasOwnProperty(k)) {
                    dest[k] = src[k];
                }
            }
        }
        return dest;
    }

    /**
     * 拷贝对象属性
     * @param  {object} target 拷贝到对象
     * @param  {object} source 被拷贝对象
     * @return {undefined}
     */
    static copy(target, source) {
        for (const key of Reflect.ownKeys(source)) {
            if (key !== 'constructor' &&
                key !== 'prototype' &&
                key !== 'name'
            ) {
                const desc = Object.getOwnPropertyDescriptor(source, key);
                Object.defineProperty(target, key, desc);
            }
        }
    }

    /**
     * 类的合并
     * @param  {...class|function|object}  mixins 待合并的类/对象
     * @return {class}                            新生成的合并对象
     */
    static mixin(...mixins) {
        class Mix {
        }

        for (const it of mixins) {
            this.copy(Mix, it); // 拷贝实例属性
            if (it.prototype) {
                this.copy(Mix.prototype, it.prototype); // 拷贝原型属性
            }
        }

        return Mix;
    }

    static without(array, other) {
        return _array.without(array, other);
    }

    static bigIntParser(value) {
        return JSONbig.parse(value);
    }

    static getCanvas() {
        return ctx;
    }
}
